import type {
  PropertyDescriptor,
  ShaderPass,
  UniformValue,
  VideoFilterDefinition,
  VideoFilterRuntime,
} from '../../core/definitions';
import type { SettingsBag } from '../../core/model';

export interface ShaderFilterSpec<S extends SettingsBag> {
  kind: string;
  displayName: string;
  defaults(): S;
  properties(settings: S): PropertyDescriptor[];
  /**
   * Fragment body of each pass, in order, following the ShaderPass convention
   * of core/definitions.ts. The strings are fixed for the life of the filter.
   */
  passes: readonly string[];
  /**
   * Uniform values for one pass. Evaluated when the filter is created and on
   * every settings change (not per frame); `settings` already has defaults
   * filled in for missing keys. uSource, uSourceSize and uTime are supplied
   * by the renderer.
   */
  uniforms?(settings: S, passIndex: number): Record<string, UniformValue>;
  /** Return false when these settings make the filter an identity; it is then bypassed at no cost. */
  active?(settings: S): boolean;
}

const NO_PASSES: readonly ShaderPass[] = Object.freeze([]);

/**
 * Builds a video filter definition from fragment shaders and a pure
 * settings-to-uniforms function. Suitable for any filter without per-instance
 * state or asynchronous resources.
 */
export function defineShaderFilter<S extends SettingsBag>(spec: ShaderFilterSpec<S>): VideoFilterDefinition<S> {
  return {
    type: 'video',
    kind: spec.kind,
    displayName: spec.displayName,
    defaults: spec.defaults,
    properties: spec.properties,
    create(_ctx, initial): VideoFilterRuntime<S> {
      const passes: ShaderPass[] = spec.passes.map((fragment) => ({ fragment, uniforms: {} }));
      let active = true;
      const apply = (settings: S): void => {
        const merged = { ...spec.defaults(), ...settings };
        active = spec.active ? spec.active(merged) : true;
        for (let i = 0; i < passes.length; i++) passes[i]!.uniforms = spec.uniforms ? spec.uniforms(merged, i) : {};
      };
      apply(initial);
      return {
        passes: () => (active ? passes : NO_PASSES),
        update: apply,
        destroy: () => undefined,
      };
    },
  };
}
